import FormField from "@src/components/FormField/FormField";
import * as yup from "yup";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Input from "@src/components/Input/Input";
import SearchInput, { Item } from "@src/components/SearchInput/SearchInput";
import { useSearchCharacter } from "@src/hooks/useSearchCharacter";
import { Controller } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "@src/components/Button/Button";
import { useCallback, useEffect, useRef, useState } from "react";
import FileInput from "@src/components/FileInput/FileInput";
import {
    CharacterListVersion,
    CharacterWithVersion,
    Version,
    World,
    WorldDTO,
} from "@src/types/global";
import { useNavigate } from "react-router";
import toast from "react-hot-toast";
import { getAllCharacters } from "@src/services/requests/characters";
import { addToArray } from "@src/utils/array";
import VersionPicker from "@src/components/VersionPicker/VersionPicker";
import { transformToCharacterListVersion } from "@src/utils/character-utils";
import { XMarkIcon } from "@heroicons/react/24/solid";
import Modal from "@src/components/Modal/Modal";
import "./styles/world-form.sass";
import "../styles/form.sass";

const schema = yup.object().shape({
    id: yup.string(),
    name: yup.string().required(),
    character_versions_ids: yup.array().of(yup.string()),
    cover_image: yup.mixed(),
    is_public: yup.boolean().default(false),
});

export interface FormValues extends yup.InferType<typeof schema> {}
interface IProps {
    submitFn: (data: FormValues) => Promise<{ data: WorldDTO } | void>;
    defaultValues?: World;
}
const WorldForm = ({
    submitFn,
    defaultValues = {
        name: "Your world",
        is_public: false,
    } as World,
}: IProps) => {
    const navigate = useNavigate();
    const [formValues, setFormValues] = useState<World>(defaultValues);
    const [isVersionPickerOpen, setIsVersionPickerOpen] =
        useState<boolean>(false);
    const [editableCharacter, setEditableCharacter] =
        useState<CharacterListVersion | null>(null);

    const methods = useForm<FormValues>({
        resolver: yupResolver(schema),
        defaultValues: formValues as FormValues,
    });
    const {
        handleSubmit,
        formState: { errors },
        register,
        control,
        getValues,
        setValue,
        reset,
    } = methods;
    const inputRef = useRef<HTMLInputElement>(null);
    const { data: charactersData, isSuccess } = useQuery({
        queryFn: getAllCharacters,
        queryKey: ["characters", "all_versions"],
        select: (data) => {
            return data.characters;
        },
    });
    const queryClient = useQueryClient();
    const mutationQuery = useMutation({
        mutationFn: submitFn,
        mutationKey: ["worlds", defaultValues?.id || "new"],
        onSuccess: (response: { data: WorldDTO } | void) => {
            const { data } = response || {};
            if (!data) return;
            toast.success("Success!");
            queryClient.setQueryData(["worlds", data.id], () => {
                return data as any as World;
            });
            navigate("/worlds");
        },
    });

    const createData = useCallback(() => {
        const character_versions_ids = [] as string[];
        const charactersWithVersion =
            defaultValues?.character_versions?.map((version) => {
                character_versions_ids.push(version.id);
                const matchingCharacter =
                    charactersData?.find(
                        (char) => char.id === version.character_id,
                    ) || {};
                //@ts-expect-error yes I know
                matchingCharacter.character_versions = version;
                return transformToCharacterListVersion(
                    matchingCharacter as any as CharacterWithVersion,
                );
            }) || [];
        return { charactersWithVersion, character_versions_ids };
    }, [defaultValues]);
    const { charactersWithVersion } = createData();
    const [characterVersions, setCharacterVersions] = useState<
        CharacterListVersion[]
    >(charactersWithVersion);
    useEffect(() => {
        console.log("calling use effect");
        if (isSuccess) {
            if (defaultValues?.character_versions) {
                const { character_versions_ids } = createData();
                setValue("character_versions_ids", character_versions_ids);
            }
            if (formValues.name !== defaultValues?.name) {
                const { charactersWithVersion, character_versions_ids } =
                    createData();
                const newValues = structuredClone(defaultValues) as World;
                //@ts-expect-error I know it doesn't exist
                newValues.character_versions_ids = character_versions_ids || [];
                reset(newValues as FormValues);
                setValue("character_versions_ids", character_versions_ids);
                setCharacterVersions(charactersWithVersion);
                setFormValues(newValues);
            }
        }
    }, [defaultValues]);
    const onSubmit: SubmitHandler<FormValues> = async (data) => {
        mutationQuery.mutate(data);
    };
    function resetVersionPickerState() {
        setEditableCharacter(null);
        setIsVersionPickerOpen(false);
    }
    function handleUseVersion(
        character: CharacterWithVersion | CharacterListVersion,
    ) {
        let characterListVersion;
        const prevValue = getValues("character_versions_ids") || [];
        if (character.hasOwnProperty("version_id")) {
            characterListVersion = character as CharacterListVersion;
            if (prevValue.includes(characterListVersion.version_id))
                return resetVersionPickerState();
            const newValue = addToArray(
                characterListVersion.version_id,
                prevValue,
            );
            setValue("character_versions_ids", newValue);
        } else {
            //@ts-expect-error adding this on purpose
            const version = character.character_versions;
            if (prevValue?.includes(version.id)) return;
            const newValue = addToArray(version.id, prevValue);
            setValue("character_versions_ids", newValue);
            characterListVersion = transformToCharacterListVersion(
                character as any as CharacterWithVersion,
            );
        }
        const newState = addToArray<CharacterListVersion>(
            characterListVersion,
            characterVersions,
        );
        setCharacterVersions(newState);
        resetVersionPickerState();
    }
    function handleCharacterSelection(selectedItem: Item) {
        if (!charactersData || !Boolean(charactersData.length)) return;
        const character = charactersData.find(
            (char) => char.default_version.id == selectedItem.id,
        );
        const characterWithVersion = structuredClone(
            character,
        ) as any as CharacterWithVersion;
        //@ts-expect-error Doing this on purpose again
        characterWithVersion.character_versions = character?.default_version;
        const shouldPickDefaultAutomatically =
            !character ||
            !character.character_versions ||
            (character.character_versions &&
                character?.character_versions?.length < 2);
        if (shouldPickDefaultAutomatically) {
            handleUseVersion(characterWithVersion);
        } else {
            setIsVersionPickerOpen(true);
            setEditableCharacter(
                transformToCharacterListVersion(characterWithVersion),
            );
        }
    }

    function handleRemove(idToRemove: string) {
        const newState = Object.values(characterVersions).filter(
            (char) => char.version_id !== idToRemove,
        );
        setCharacterVersions(newState);
        const prevValues = getValues("character_versions_ids") || [];
        if (prevValues?.includes(idToRemove)) {
            const newValues = prevValues.filter(
                (value) => value !== idToRemove,
            );
            setValue("character_versions_ids", newValues);
        }
    }
    const { searchLoading, displayResults, setDisplayResults, handleChange } =
        useSearchCharacter();

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <input {...register("id")} name="id" type="hidden" />
            <FormField errors={errors} name="name" displayName="World Name">
                <Input
                    type="text"
                    name="name"
                    placeholder="Default"
                    register={register}
                />
            </FormField>
            <FormField errors={errors} name="is_public" hideLabel={true}>
                <Input
                    type="checkbox"
                    name="is_public"
                    placeholder="Is public?"
                    label="Is this world public?"
                    register={register}
                />
            </FormField>
            <FormProvider {...methods}>
                <Controller
                    shouldUnregister={true}
                    control={control}
                    name="cover_image"
                    render={({ field }) => {
                        return (
                            <FormField
                                errors={errors}
                                displayName="Cover image"
                                name={field.name}
                                key={field.value}
                            >
                                <FileInput
                                    inputName={field.name}
                                    hideLabel={true}
                                    savedImagePath={field.value}
                                    parentFunction={(dataUrl) => {
                                        setValue(field.name, dataUrl);
                                    }}
                                />
                            </FormField>
                        );
                    }}
                />
            </FormProvider>
            <FormField
                name="characters"
                displayName="Characters (versions)"
                errors={errors}
            >
                <Controller
                    control={control}
                    name="character_versions_ids"
                    render={({ field }) => {
                        return (
                            <SearchInput
                                placeholder="Enter a name here..."
                                name={field.name}
                                searchResults={displayResults}
                                onChange={handleChange}
                                onClick={handleCharacterSelection}
                                onClickOutside={() => setDisplayResults([])}
                                isLoading={searchLoading}
                                ref={inputRef}
                            />
                        );
                    }}
                />
                <div className="world-form__selected-characters">
                    {characterVersions?.map((char) => (
                        <div
                            key={char.version_id}
                            className="world-form__selected-characters__item"
                        >
                            <span>
                                {char.first_name}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setEditableCharacter(char);
                                        setIsVersionPickerOpen(true);
                                    }}
                                    title="Change version"
                                    className="world-form__selected-characters__item__version-chip"
                                >
                                    {char.version_name}
                                </button>
                            </span>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleRemove(char.version_id);
                                }}
                                className="world-form__selected-characters__item__remove"
                                title="Remove from selection."
                            >
                                <XMarkIcon />
                                Remove
                            </button>
                        </div>
                    ))}
                </div>
            </FormField>

            <Modal
                isOpen={isVersionPickerOpen}
                closeModalFn={() => setIsVersionPickerOpen(false)}
                actions={
                    <Button
                        preset="secondary"
                        fn={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (editableCharacter)
                                handleUseVersion(editableCharacter);
                        }}
                    >
                        Use default value
                    </Button>
                }
            >
                {isVersionPickerOpen && editableCharacter && (
                    <VersionPicker
                        character={editableCharacter}
                        onClick={(
                            version: Version,
                            originalCharacterObject: CharacterListVersion,
                        ) => {
                            const character = structuredClone(
                                originalCharacterObject,
                            );
                            //@ts-expect-error adding the character_versions key on purpose so that the item gets transformed
                            character.character_versions = version;
                            const { version_id, ...characterWithoutVersionId } =
                                character;
                            handleUseVersion(
                                characterWithoutVersionId as any as CharacterWithVersion,
                            );
                            resetVersionPickerState();
                        }}
                    />
                )}
            </Modal>
            <div>
                {errors &&
                    Object.values(errors)?.map((error, i) => (
                        <li key={i}>{error?.message?.toString()}</li>
                    ))}
            </div>
            <Button preset="primary" type="submit">
                Submit
            </Button>
        </form>
    );
};
export default WorldForm;
